import { isApiError } from '@/api/errors'

/**
 * Why a newsletter call failed, in the terms a screen can act on.
 *
 * ── Branch on `error.code`. Never on status, never on the message. ───────────
 *
 * The code that matters is `NEWSLETTER_NOT_FOUND`
 * (`services/newsletter/exceptions.py`), and it covers THREE situations the
 * backend deliberately refuses to tell apart, because distinguishing them
 * would leak the existence of drafts:
 *
 *   - the issue number does not exist
 *   - the issue exists but is not PUBLISHED
 *   - the key was not usable at all
 *
 * All three are "there is no such issue to read" and all three are a
 * not-found SCREEN — a "we could not find that issue, here is the archive"
 * state — not the generic failure card a 500 gets. That is the distinction
 * this module exists to make, and status alone cannot make it: the generic
 * `NOT_FOUND` is a 404 too and means something else entirely (our own URL is
 * wrong), while a proxy's HTML 502 arrives as a synthesised `NOT_FOUND` from
 * `toApiError` without ever having been a missing issue.
 *
 * On `latest`, the same code means "nothing has been published yet" rather
 * than "that issue is gone". The response cannot say which, so the call site
 * distinguishes them by knowing which route it asked for — see
 * `emptyArchiveMessage`.
 *
 * Note for whoever regenerates `@/api/errors`: `NEWSLETTER_NOT_FOUND` is not
 * yet in `ERROR_CODES`. It type-checks regardless because `AnyErrorCode`
 * admits any string, but the constant list should gain it. That file is not
 * this feature's to edit.
 */
export type NewsletterFailure =
  /**
   * No such readable issue. The screen renders a not-found state and offers
   * the archive; it must NOT retry, because nothing about the request will
   * change the answer.
   */
  | { kind: 'issueNotFound' }
  /**
   * Everything else: a 5xx, a transport failure, a `NetworkError` from a body
   * that was not the v5 envelope, and the generic `NOT_FOUND` that means we
   * built a bad URL. Retryable, and worth a plain failure card.
   *
   * There is no `signInRequired` branch on purpose. All three routes are
   * `V5PublicAPIView`, so a 401 from here is not an expired session — it is a
   * misconfiguration, and treating it as a session expiry would sign a
   * signed-in member out for reading a public page.
   */
  | { kind: 'failed' }

/** mm-be's code for "no such readable issue" — unknown, unpublished, or bad key. */
export const NEWSLETTER_NOT_FOUND = 'NEWSLETTER_NOT_FOUND'

/**
 * Classifies a rejection. `null` when there is nothing to classify, so a
 * screen can write `newsletterFailure(error)` straight off a query result.
 */
export const newsletterFailure = (error: unknown): NewsletterFailure | null => {
  if (error === null || error === undefined) return null
  if (!isApiError(error)) return { kind: 'failed' }
  return error.code === NEWSLETTER_NOT_FOUND ? { kind: 'issueNotFound' } : { kind: 'failed' }
}

/** True when a screen should render its not-found state rather than an error. */
export const isIssueNotFound = (error: unknown): boolean =>
  newsletterFailure(error)?.kind === 'issueNotFound'

/**
 * Copy for a failure, written here rather than taken from `error.message`.
 *
 * The backend's messages are English, unlocalised and explicitly for logs; an
 * unhandled 500 may not be enveloped at all, so there may be no message to
 * read. The mapping is one-way: a code in, our own sentence out.
 *
 * RECOMPOSED AGAINST V2 (`4243:118206`, "Newsletters · Failed", read at full
 * resolution): the `'failed'` copy is now the frame's own words, "Check your
 * internet and try again.", replacing "Something went wrong. Please try
 * again." — a previous pass's own wording, written before any frame for this
 * state had been captured, and left disagreeing with the frame even after a
 * thumbnail confirmed it (flagged then as out of this-pass's file ownership;
 * `errors.ts` is in scope for this pass, so the disagreement is resolved
 * rather than re-flagged). Both call sites share this string —
 * `NewslettersScreen`'s failed branch (confirmed against this exact frame)
 * and `NewsletterIssueScreen`'s `IssueFailed` (no reader-failure frame exists
 * in this family's seven; unconfirmed there, but no less plausible a
 * sentence for a reader that also failed to load over the network).
 */
export const failureMessage = (failure: NewsletterFailure): string => {
  switch (failure.kind) {
    case 'issueNotFound':
      return 'We could not find that issue of MoneyMitra Weekly.'
    case 'failed':
      return 'Check your internet and try again.'
  }
}

/**
 * The same code, said the way the `latest` route means it.
 *
 * `GET /latest/` raises `NEWSLETTER_NOT_FOUND` when NOTHING is published yet,
 * which is an empty state, not a broken link. Only the caller knows which
 * route it asked for, so the wording is chosen here rather than in
 * `failureMessage`.
 *
 * RECOMPOSED AGAINST V2 (`4243:117490`, "Newsletters · Empty", read at full
 * resolution): the copy is quoted verbatim off that render, replacing the
 * previous "No issues have been published yet." — a summary this pass wrote
 * itself rather than one taken from a frame, back when only a thumbnail
 * capture existed. The new copy also does real work the panel doesn't
 * otherwise say: it says when the first issue comes out. (The frame's "Turn on
 * Get new issues" sentence is cut: that row had no subscribe endpoint behind
 * it and is gone.)
 */
export const emptyArchiveMessage = (failure: NewsletterFailure): string =>
  failure.kind === 'issueNotFound'
    ? 'The first MoneyMitra Weekly comes out on Sunday morning.'
    : failureMessage(failure)
